import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, IconButton, CircularProgress, Alert, Toolbar, TextField, InputAdornment, Pagination, PaginationItem, FormControl, Select, MenuItem, Typography
} from '@mui/material';
import { Check as ApproveIcon, Search as SearchIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import api from '../services/api';

const PaymentsTable = () => {
  const [data, setData] = useState({
    payments: [],
    totalPayments: 0,
    recentUsers: [], // Adicionado para armazenar os usuários
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1); 
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPayments = useCallback(async () => {
    try {
      const paymentsResponse = await api.get(`/payments_user?page=${page}&limit=${rowsPerPage}`);
      const usersResponse = await api.get('/users'); // Buscar todos os usuários

      setData({
        payments: paymentsResponse.data.payments,
        totalPayments: paymentsResponse.data.totalPayments,
        recentUsers: usersResponse.data.users, // Armazenar os usuários
      });
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch payments', error);
      setError('Failed to fetch payments');
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleApprovePayment = async (payment) => {
    try {
      const userId = payment.id_user;
      const user = data.recentUsers.find((u) => u.id === userId);

      if (user && user.status === 0) {
        await api.put(`/users/update-status/${userId}`, { status: 1 });

        setData((prevData) => ({
          ...prevData,
          recentUsers: prevData.recentUsers.map((u) =>
            u.id === userId ? { ...u, status: 1 } : u
          ),
          payments: prevData.payments.map((p) =>
            p.id === payment.id ? { ...p, status: 'approved' } : p
          ),
        }));

        Swal.fire('Sucesso!', 'Pagamento aprovado e status do usuário atualizado com sucesso.', 'success');
      } else {
        Swal.fire('Aviso!', 'Usuário já está ativo.', 'info');
      }
    } catch (error) {
      console.error('Failed to approve payment', error);
      Swal.fire('Erro!', 'Falha ao aprovar o pagamento e atualizar o status do usuário.', 'error');
    }
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredPayments = data.payments ? data.payments.filter(payment =>
    payment.txtId.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'Roboto' }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Box className="payments-container" sx={{ width: '50%', padding: 3, backgroundColor: 'white', borderRadius: 2, boxShadow: 3, fontFamily: 'Roboto' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 3 }}>Lista de Pagamentos</Typography>
        <Toolbar sx={{ height: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
          <TextField
            placeholder="Buscar..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ width: '30%', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { fontFamily: 'Roboto' },
            }}
            InputLabelProps={{ style: { fontFamily: 'Roboto' } }}
          />
          <FormControl sx={{ minWidth: 60 }}>
            <Select
              labelId="rows-per-page-label"
              id="rows-per-page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ height: '40px', fontFamily: 'Roboto' }}
              inputProps={{ style: { fontFamily: 'Roboto' } }}
            >
              <MenuItem value={5} sx={{ fontFamily: 'Roboto' }}>5</MenuItem>
              <MenuItem value={10} sx={{ fontFamily: 'Roboto' }}>10</MenuItem>
              <MenuItem value={25} sx={{ fontFamily: 'Roboto' }}>25</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', width: '100%' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">ID Usuário</TableCell>
                <TableCell align="center">txtId</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell align="center">{payment.id}</TableCell>
                  <TableCell align="center">{payment.id_user}</TableCell>
                  <TableCell align="center">{payment.txtId}</TableCell>
                  <TableCell align="center">
                    <IconButton color="primary" aria-label="approve payment" onClick={() => handleApprovePayment(payment)}>
                      <ApproveIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(data.totalPayments / rowsPerPage)}
            page={page}
            onChange={handleChangePage}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{ '&.Mui-selected': { backgroundColor: 'primary.main', color: 'white', fontFamily: 'Roboto' } }}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentsTable;
